import React, { useRef, useState } from "react";
import style from "./timer-input.module.css";
import { useHover } from "frontend/ui-components/floaters/use-hover";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import { CanvasTimer } from "frontend/hooks/use-timer";

const minusButton = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M2 9H18V11.087H2V9Z" fill="currentColor" />
  </svg>
);

const plusButton = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.0435 8.95652V2H8.95652V8.95652H2V11.0435H8.95652V18H11.0435V11.0435H18V8.95652H11.0435Z"
      fill="currentColor"
    />
  </svg>
);

const TimerInput = ({
  timer,
  setOnEditEnded,
  isReadOnly,
}: {
  timer: CanvasTimer;
  setOnEditEnded: (edit: boolean) => void;
  isReadOnly: boolean;
}) => {
  const [secondsInput, setSecondsInput] = useState<string>("");
  const [minutesInput, setMinutesInput] = useState<string>("");

  const minusButtonRef = useRef<any>(null);
  const plusButtonRef = useRef<any>(null);

  const secondsInputRef = useRef<any>(null);
  const minutesInputRef = useRef<any>(null);

  const minusButtonHover = useHover(minusButtonRef);
  const plusButtonHover = useHover(plusButtonRef);

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;
    const filteredValue = value.replace(/[^0-9]/g, ""); // Filter out non-numeric characters
    if (name === "minutes") {
      filteredValue.length === 2 && secondsInputRef.current.focus();
      setMinutesInput(filteredValue);
    } else {
      setSecondsInput(filteredValue);
    }
  };

  const handleSecondsSubmit = (event: any) => {
    event.preventDefault();
    const { value } = event.target;
    secondsInputRef.current.blur();
    let minutes = Number(timer.minutesString);
    let seconds = value.length ? Number(value) : Number(timer.secondsString);
    if (seconds > 59) {
      minutes = Number(timer.minutesString) + Math.floor(seconds / 60);
      seconds = seconds % 60;
    }
    timer.set(minutes * 60 + seconds);
    setSecondsInput(seconds.toString().padStart(2, "0"));
    setOnEditEnded(timer.config.pause !== 0);
  };

  const handleMinutesSubmit = (event: any) => {
    //TODO: support paste
    event.preventDefault();
    const { value } = event.target;
    minusButtonRef.current.blur();
    let minutes = value.length ? Number(value) : Number(timer.minutesString);
    let seconds = Number(timer.secondsString);
    if (minutes > 99) {
      minutes = Math.floor(minutes / 100);
      seconds = Math.floor(minutes % 100);
    }
    timer.set(minutes * 60 + seconds);
    setMinutesInput(minutes.toString().padStart(2, "0"));
    setSecondsInput(seconds.toString().padStart(2, "0"));
    setOnEditEnded(timer.config.pause !== 0);
  };

  const handleInputFocus = (event: any) => {
    if (!isReadOnly) {
      event.target.select();
      timer.pause();
      event.target.name === "seconds" ? setSecondsInput("") : setMinutesInput("");
    }
  };

  function renderInputButton(ref: any, onClick: () => void, buttonSvg: any, hover: any, label: string, testId: string) {
    return (
      <div ref={ref}>
        <button className={style.changeTimeButton} onClick={onClick} data-testid={testId}>
          {buttonSvg}
        </button>
        {hover && <Tooltip side="top" label={label} relativeTo={ref} boundary="none" />}
      </div>
    );
  }
  return (
    <div className={style.timerInput} style={{ marginTop: isReadOnly ? "30px" : "45px" }}>
      {!isReadOnly &&
        renderInputButton(
          minusButtonRef,
          timer.decrement,
          minusButton,
          minusButtonHover,
          "Less minutes",
          "decrement-minute"
        )}
      <div>
        <input
          className={style.inputField}
          type="text"
          name={"minutes"}
          ref={minutesInputRef}
          autoComplete="off"
          value={minutesInputRef.current == document.activeElement ? minutesInput : timer.minutesString}
          placeholder={timer.minutesString}
          maxLength={3}
          onChange={handleInputChange}
          onBlur={handleMinutesSubmit}
          onKeyDown={(e) => {
            e.key === "Enter" && handleMinutesSubmit(e);
          }}
          onFocus={handleInputFocus}
          data-testid={"timer-minutes-input"}
          readOnly={isReadOnly}
        />
        <span>:</span>
        <input
          className={style.inputField}
          type="text"
          name={"seconds"}
          ref={secondsInputRef}
          autoComplete="off"
          value={secondsInputRef.current == document.activeElement ? secondsInput : timer.secondsString}
          placeholder={timer.secondsString}
          maxLength={2}
          onChange={handleInputChange}
          onBlur={handleSecondsSubmit}
          onKeyDown={(e) => {
            e.key === "Enter" && handleSecondsSubmit(e);
          }}
          onFocus={handleInputFocus}
          data-testid={"timer-seconds-input"}
          readOnly={isReadOnly}
        />
      </div>
      {!isReadOnly &&
        renderInputButton(
          plusButtonRef,
          timer.increment,
          plusButton,
          plusButtonHover,
          "More minutes",
          "increment-minute"
        )}
    </div>
  );
};

export default TimerInput;
